import React, { useState, useEffect, useRef } from "react";
import {
  StandardizedTable,
  TableColumn,
  formatNumber,
  formatCurrency,
  TrendIndicator,
} from "@/components/ui/standardized-table";
import { Skeleton } from "@/components/ui/skeleton";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { useAllPagesData } from "@/hooks/useAllPagesData";
import { useLandingPageData } from "@/hooks/useLandingPageData";
import { usePagesSourceMedium } from "@/hooks/usePagesSourceMedium";
import { usePagesCampaignsData } from "@/hooks/usePagesCampaignsData";
import type { TableQueryDTO, TableFilterOperator } from "@/types/table-query";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";
import { getChannelGroupingCardDataApiV1ChannelGroupingCardGet } from "@/backend-sdk/client/sdk.gen";
import type { ChannelGroupingCardItem as GeneratedChannelGroupingCardItem } from "@/backend-sdk/client/types.gen";

type ChannelGroupingCardItem = GeneratedChannelGroupingCardItem;

type ChannelGroupingRow = {
  grupoCanais: string;
  sessoes: number;
  sessoesChange: number;
  sessoesTrend: string;
  usuarios: number;
  usuariosChange: number;
  usuariosTrend: string;
  txConversao: number;
  txConversaoChange: number;
  txConversaoTrend: string;
  pedidos: number;
  pedidosChange: number;
  pedidosTrend: string;
  tm: number;
  tmChange: number;
  tmTrend: string;
  receitaTotal: number;
  receitaTotalChange: number;
  receitaTotalTrend: string;
};

const FILTER_OPERATORS: { value: TableFilterOperator; label: string }[] = [
  { value: "equals", label: "Igual a" },
  { value: "contains", label: "Contém" },
  { value: "startsWith", label: "Começa com" },
  { value: "regex", label: "Regex" },
  { value: "in", label: "Em" },
];

/** Campo de busca com dropdown de operador */
const SearchField = ({
  label,
  value,
  operator,
  onValueChange,
  onOperatorChange,
}: {
  label: string;
  value: string;
  operator: TableFilterOperator;
  onValueChange: (v: string) => void;
  onOperatorChange: (op: TableFilterOperator) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedLabel =
    FILTER_OPERATORS.find((o) => o.value === operator)?.label ?? "Contém";

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <label className="block text-xs font-semibold mb-1 text-muted-foreground">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1 text-xs font-medium cursor-pointer select-none text-muted-foreground hover:text-foreground"
          >
            {selectedLabel}
            <span>▾</span>
          </button>
          {open && (
            <div className="absolute left-0 top-full mt-1 z-50 min-w-[140px] rounded-md border border-border bg-card shadow-lg py-1">
              {FILTER_OPERATORS.map((op) => (
                <button
                  key={op.value}
                  type="button"
                  onClick={() => {
                    onOperatorChange(op.value);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 ${
                    op.value === operator
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          type="text"
          placeholder="Insira um valor"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder-muted-foreground"
        />
      </div>
    </div>
  );
};

// DADOS MOCKADOS REMOVIDOS - Usando dados reais do endpoint /dashboard/channel-grouping-card

// DADOS MOCKADOS REMOVIDOS - Implementado componente CampanhasTable com dados reais

// Props compartilhadas de busca
type PageSearchProps = {
  searchTitle: string;
  searchTitleOp: TableFilterOperator;
  searchPath: string;
  searchPathOp: TableFilterOperator;
  campaignFiltersNonce: number;
};

// Componente para a tabela "TODAS AS PÁGINAS" - COM DADOS REAIS
const TodasAsPaginasTable = ({
  searchTitle,
  searchTitleOp,
  searchPath,
  searchPathOp,
  campaignFiltersNonce,
}: PageSearchProps) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  // Combinar filtros locais com filtros globais de busca
  const mergedQuery: TableQueryDTO = React.useMemo(() => {
    const filters: TableQueryDTO["filters"] = [...(tableQuery.filters || [])];
    if (searchTitle.trim())
      filters.push({
        columnKey: "pageTitle",
        operator: searchTitleOp,
        value: searchTitle.trim(),
      });
    if (searchPath.trim())
      filters.push({
        columnKey: "pagePath",
        operator: searchPathOp,
        value: searchPath.trim(),
      });
    return { ...tableQuery, filters: filters.length ? filters : undefined };
  }, [tableQuery, searchTitle, searchTitleOp, searchPath, searchPathOp]);

  // Reset page quando busca muda
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchTitle, searchPath]);

  const startDate = formatDateToISO(filterState.dateRange.startDate);
  const endDate = formatDateToISO(filterState.dateRange.endDate);
  const comparison =
    filterState.comparison?.type === "previousYear"
      ? "previous_year"
      : "previous_period";
  const customerUuid =
    localStorage.getItem("active_customer_uuid") ||
    selectedAccount?.customer_uuid ||
    selectedAccount?.id;
  const accountId = selectedAccount?.account_id ?? undefined;

  const { data, totals, pagination, loading, error } = useAllPagesData(
    startDate,
    endDate,
    comparison,
    customerUuid || undefined,
    accountId || undefined,
    currentPage,
    10,
    mergedQuery,
    campaignFiltersNonce,
  );

  const columns: TableColumn[] = [
    {
      key: "pageTitle",
      header: "Título da página",
      align: "left",
      render: (value, item, index, isTotal) => {
        if (isTotal) return value;
        const displayValue = value || "N/D";
        return `${(currentPage - 1) * 10 + index + 1}. ${displayValue}`;
      },
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "sessions_percent_change",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "-";
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "activeUsers",
      header: "Usuários ativos",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "activeUsers_percent_change",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "-";
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "screenPageViews",
      header: "Visualizações",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "screenPageViews_percent_change",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "-";
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "transactions",
      header: "Pedidos",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "transactions_percent_change",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "-";
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "purchaseRevenue",
      header: "Receita total",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatCurrency(value) : "N/D",
    },
    {
      key: "purchaseRevenue_percent_change",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (value === null || value === undefined) return "-";
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right",
      visible: false,
      render: (_value, item) => {
        const revenue = Number(item.purchaseRevenue ?? 0);
        const users = Number(item.activeUsers ?? 0);
        if (users === 0) return "N/D";
        return formatCurrency(revenue / users);
      },
    },
  ];

  // Usar totais do endpoint (totais reais de TODOS os registros)
  const totalRow = totals || null;

  if (loading && data.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
        <h3 className="font-semibold mb-2">Erro ao carregar dados</h3>
        <p className="text-sm">{error}</p>
        <p className="text-sm mt-2">
          Verifique a conexão de seus dados na tela Integrações ou a
          configuração de suas fontes de dados nos devidos portais (Google,
          Meta, etc).
        </p>
      </div>
    );
  }

  if (!customerUuid) {
    return (
      <div className="bg-yellow-50 text-yellow-800 p-6 rounded-lg border border-yellow-200">
        <h3 className="font-semibold mb-2">Atenção</h3>
        <p className="text-sm">
          Nenhuma conta selecionada. Por favor, selecione uma conta para
          visualizar os dados.
        </p>
      </div>
    );
  }

  return (
    <StandardizedTable
      title="TODAS AS PÁGINAS"
      columns={columns}
      data={data}
      total={totalRow ?? undefined}
      loading={loading}
      pagination={{
        current: currentPage,
        pageSize: 10,
        total: pagination.total_items,
        onPageChange: (page) => setCurrentPage(page),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

// Componente para a tabela "PÁGINA DE ENTRADA" - COM DADOS REAIS
const PaginaEntradaTable = ({
  searchTitle,
  searchTitleOp,
  searchPath,
  searchPathOp,
  campaignFiltersNonce,
}: PageSearchProps) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const mergedQuery: TableQueryDTO = React.useMemo(() => {
    const filters: TableQueryDTO["filters"] = [...(tableQuery.filters || [])];
    if (searchTitle.trim())
      filters.push({
        columnKey: "pageTitle",
        operator: searchTitleOp,
        value: searchTitle.trim(),
      });
    if (searchPath.trim())
      filters.push({
        columnKey: "pagePath",
        operator: searchPathOp,
        value: searchPath.trim(),
      });
    return { ...tableQuery, filters: filters.length ? filters : undefined };
  }, [tableQuery, searchTitle, searchTitleOp, searchPath, searchPathOp]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchTitle, searchPath]);

  const startDate = formatDateToISO(filterState.dateRange.startDate);
  const endDate = formatDateToISO(filterState.dateRange.endDate);
  const comparison =
    filterState.comparison?.type === "previousYear"
      ? "previous_year"
      : "previous_period";
  const customerUuid =
    localStorage.getItem("active_customer_uuid") ||
    selectedAccount?.customer_uuid ||
    selectedAccount?.id;
  const accountId = selectedAccount?.account_id ?? undefined;

  const { data, totals, pagination, loading, error } = useLandingPageData({
    startDate,
    endDate,
    comparison,
    customerUuid: customerUuid || undefined,
    accountId: accountId || undefined,
    page: currentPage,
    pageSize: 10,
    tableQuery: mergedQuery,
    campaignFiltersNonce,
  });

  const columns: TableColumn[] = [
    {
      key: "paginaDestino",
      header: "Página de destino",
      align: "left",
      render: (value, item, index, isTotal) => {
        if (isTotal) return value;
        const displayValue = value || "N/D";
        return `${(currentPage - 1) * 10 + index + 1}. ${displayValue}`;
      },
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "sessoes_trend_indicator",
      header: "% Δ",
      align: "right",
      render: (value, item) => {
        const percentChange = item?.sessoes_percent_change;
        if (percentChange === null || percentChange === undefined) return "-";
        return <TrendIndicator value={percentChange} />;
      },
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "pedidos_trend_indicator",
      header: "% Δ",
      align: "right",
      render: (value, item) => {
        const percentChange = item?.pedidos_percent_change;
        if (percentChange === null || percentChange === undefined) return "-";
        return <TrendIndicator value={percentChange} />;
      },
    },
    {
      key: "valorSessao",
      header: "Valor Sessão",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatCurrency(value) : "N/D",
    },
    {
      key: "valorSessao_trend_indicator",
      header: "% Δ",
      align: "right",
      render: (value, item) => {
        const percentChange = item?.valorSessao_percent_change;
        if (percentChange === null || percentChange === undefined) return "-";
        return <TrendIndicator value={percentChange} />;
      },
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? `${value.toFixed(2)}%` : "N/D",
    },
    {
      key: "txConversao_trend_indicator",
      header: "% Δ",
      align: "right",
      render: (value, item) => {
        const percentChange = item?.txConversao_percent_change;
        if (percentChange === null || percentChange === undefined) return "-";
        return <TrendIndicator value={percentChange} />;
      },
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined ? formatCurrency(value) : "N/D",
    },
    {
      key: "receitaTotal_trend_indicator",
      header: "% Δ",
      align: "right",
      render: (value, item) => {
        const percentChange = item?.receitaTotal_percent_change;
        if (percentChange === null || percentChange === undefined) return "-";
        return <TrendIndicator value={percentChange} />;
      },
    },
    {
      key: "totalUsers",
      header: "Total de usuários",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined ? formatNumber(value) : "N/D",
    },
    {
      key: "totalUsers_trend_indicator",
      header: "% Δ",
      align: "right",
      render: (value, item) => {
        const percentChange = item?.totalUsers_percent_change;
        if (percentChange === null || percentChange === undefined) return "-";
        return <TrendIndicator value={percentChange} />;
      },
    },
    {
      key: "arpu",
      header: "ARPU",
      align: "right",
      visible: false,
      render: (value, item) => {
        const revenue = Number(item?.receitaTotal ?? 0);
        const users = Number(item?.totalUsers ?? 0);
        if (users === 0) return "N/D";
        return formatCurrency(revenue / users);
      },
    },
  ];

  // Usar totais do endpoint (totais reais de TODAS as landing pages)
  const totalRow = totals || null;

  if (loading && data.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
        <h3 className="font-semibold mb-2">Erro ao carregar dados</h3>
        <p className="text-sm">{error}</p>
        <p className="text-sm mt-2">
          Não conseguimos processar esta informação. Verifique a conexão de seus
          dados na tela Integrações ou a configuração de suas fontes de dados
          nos devidos portais (Google, Meta, etc).
        </p>
      </div>
    );
  }

  if (!customerUuid) {
    return (
      <div className="bg-yellow-50 text-yellow-800 p-6 rounded-lg border border-yellow-200">
        <h3 className="font-semibold mb-2">Atenção</h3>
        <p className="text-sm">
          Nenhuma conta selecionada. Por favor, selecione uma conta para
          visualizar os dados.
        </p>
      </div>
    );
  }

  return (
    <StandardizedTable
      title="PÁGINA DE ENTRADA"
      columns={columns}
      data={data}
      total={totalRow ?? undefined}
      loading={loading}
      pagination={{
        current: currentPage,
        pageSize: 10,
        total: pagination.total_items,
        onPageChange: (page) => setCurrentPage(page),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

// Componente para a tabela "Agrupamento de canal padrão" - COM DADOS REAIS
const AgrupamentoCanalTable = ({
  campaignFiltersNonce,
}: {
  campaignFiltersNonce: number;
}) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const [channelGroupingData, setChannelGroupingData] = useState<
    ChannelGroupingRow[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar dados do channel grouping card
  useEffect(() => {
    const loadChannelGroupingData = async () => {
      if (!filterState.dateRange.startDate || !filterState.dateRange.endDate) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const customerUuid =
          localStorage.getItem("active_customer_uuid") ||
          selectedAccount?.customer_uuid ||
          selectedAccount?.id;
        const accountId = selectedAccount?.account_id ?? undefined;

        const { data } =
          await getChannelGroupingCardDataApiV1ChannelGroupingCardGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              period: "7d",
              comparison_type: "previous_period",
              customer_uuid: customerUuid || undefined,
              account_id: accountId,
            },
            throwOnError: true,
          });

        // Transformar dados para o formato esperado pela tabela
        const transformedData = (data.data || []).map(
          (item: ChannelGroupingCardItem): ChannelGroupingRow => ({
            grupoCanais: item.channel_group,
            sessoes: item.sessions,
            sessoesChange: item.sessions_change_percentage,
            sessoesTrend: item.sessions_trend_indicator,
            usuarios: item.users,
            usuariosChange: item.users_change_percentage,
            usuariosTrend: item.users_trend_indicator,
            txConversao: item.conversion_rate,
            txConversaoChange: item.conversion_rate_change_percentage,
            txConversaoTrend: item.conversion_rate_trend_indicator,
            pedidos: item.transactions,
            pedidosChange: item.transactions_change_percentage,
            pedidosTrend: item.transactions_trend_indicator,
            tm: item.avg_order_value,
            tmChange: item.avg_order_value_change_percentage,
            tmTrend: item.avg_order_value_trend_indicator,
            receitaTotal: item.revenue,
            receitaTotalChange: item.revenue_change_percentage,
            receitaTotalTrend: item.revenue_trend_indicator,
          }),
        );

        setChannelGroupingData(transformedData);
      } catch (err) {
        console.error("Erro ao carregar dados do channel grouping:", err);
        setError("Erro ao carregar dados de agrupamento de canais");
        setChannelGroupingData([]);
      } finally {
        setLoading(false);
      }
    };

    loadChannelGroupingData();
  }, [
    filterState.dateRange.startDate,
    filterState.dateRange.endDate,
    selectedAccount,
    campaignFiltersNonce,
  ]);

  const columns: TableColumn[] = [
    {
      key: "grupoCanais",
      header: "Grupo de canais padrão da sessão",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      key: "sessoesChange",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "usuarios",
      header: "Usuários",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      key: "usuariosChange",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => `${value?.toFixed(2) || "0.00"}%`,
    },
    {
      key: "txConversaoChange",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      key: "pedidosChange",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "tm",
      header: "TM",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "tmChange",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "receitaTotalChange",
      header: "% Δ",
      align: "right",
      render: (value) => <TrendIndicator value={value} />,
    },
  ];

  // Calcular totais
  const totalRow =
    channelGroupingData.length > 0
      ? {
          grupoCanais: "Total Geral",
          sessoes: channelGroupingData.reduce(
            (sum, item) => sum + (item.sessoes || 0),
            0,
          ),
          sessoesChange: 0, // Não calculamos mudança percentual para total
          usuarios: channelGroupingData.reduce(
            (sum, item) => sum + (item.usuarios || 0),
            0,
          ),
          usuariosChange: 0,
          txConversao:
            channelGroupingData.length > 0
              ? channelGroupingData.reduce(
                  (sum, item) => sum + (item.txConversao || 0),
                  0,
                ) / channelGroupingData.length
              : 0,
          txConversaoChange: 0,
          pedidos: channelGroupingData.reduce(
            (sum, item) => sum + (item.pedidos || 0),
            0,
          ),
          pedidosChange: 0,
          tm:
            channelGroupingData.length > 0
              ? channelGroupingData.reduce(
                  (sum, item) => sum + (item.tm || 0),
                  0,
                ) / channelGroupingData.length
              : 0,
          tmChange: 0,
          receitaTotal: channelGroupingData.reduce(
            (sum, item) => sum + (item.receitaTotal || 0),
            0,
          ),
          receitaTotalChange: 0,
        }
      : {
          grupoCanais: "Total Geral",
          sessoes: 0,
          sessoesChange: 0,
          usuarios: 0,
          usuariosChange: 0,
          txConversao: 0,
          txConversaoChange: 0,
          pedidos: 0,
          pedidosChange: 0,
          tm: 0,
          tmChange: 0,
          receitaTotal: 0,
          receitaTotalChange: 0,
        };

  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600">{error}</p>
        <p className="text-sm text-red-500 mt-1">
          Não conseguimos processar esta informação. Verifique a conexão de seus
          dados na tela Integrações ou a configuração de suas fontes de dados
          nos devidos portais (Google, Meta, etc).
        </p>
      </div>
    );
  }

  return (
    <StandardizedTable
      title="Agrupamento de canal padrão"
      description="Grupo de canais padrão da sessão"
      columns={columns}
      data={channelGroupingData}
      total={channelGroupingData.length > 0 ? totalRow : undefined}
      loading={loading}
    />
  );
};

// Componente para a tabela "Origem / Mídia"
const OrigemMidiaTable = ({
  searchTitle,
  searchTitleOp,
  searchPath,
  searchPathOp,
  campaignFiltersNonce,
}: PageSearchProps) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const mergedQuery: TableQueryDTO = React.useMemo(() => {
    const filters: TableQueryDTO["filters"] = [...(tableQuery.filters || [])];
    if (searchTitle.trim())
      filters.push({
        columnKey: "pageTitle",
        operator: searchTitleOp,
        value: searchTitle.trim(),
      });
    if (searchPath.trim())
      filters.push({
        columnKey: "pagePath",
        operator: searchPathOp,
        value: searchPath.trim(),
      });
    return { ...tableQuery, filters: filters.length ? filters : undefined };
  }, [tableQuery, searchTitle, searchTitleOp, searchPath, searchPathOp]);

  const startDate = filterState.dateRange.startDate
    ? formatDateToISO(filterState.dateRange.startDate)
    : "";
  const endDate = filterState.dateRange.endDate
    ? formatDateToISO(filterState.dateRange.endDate)
    : "";
  const comparison =
    filterState.comparison?.type === "previousYear"
      ? "previous_year"
      : "previous_period";
  const customerUuid =
    localStorage.getItem("active_customer_uuid") ||
    selectedAccount?.customer_uuid ||
    selectedAccount?.id;
  const accountId = selectedAccount?.account_id ?? undefined;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    campaignFiltersNonce,
    searchTitle,
    searchPath,
  ]);

  const {
    data: origemMidiaData,
    total: origemMidiaTotal,
    pagination: origemMidiaPagination,
    loading: origemMidiaLoading,
    error: origemMidiaError,
  } = usePagesSourceMedium(
    startDate,
    endDate,
    comparison,
    customerUuid || undefined,
    accountId || undefined,
    currentPage,
    pageSize,
    mergedQuery,
    campaignFiltersNonce,
  );

  const columns: TableColumn[] = [
    {
      key: "origem_midia",
      header: "Origem / mídia da sessão",
      align: "left",
      render: (value, item, index, isTotal) => {
        if (isTotal) return value;
        if (origemMidiaLoading) return <Skeleton className="h-4 w-32" />;
        const position = (currentPage - 1) * pageSize + index + 1;
        return `${position}. ${value}`;
      },
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-16" />;
        return formatNumber(value);
      },
    },
    {
      key: "sessoes_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-12" />;
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "usuarios_ativos",
      header: "Usuários",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-16" />;
        return formatNumber(value);
      },
    },
    {
      key: "taxa_conversao",
      header: "TX Conversão",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-16" />;
        return `${value?.toFixed(2) || "0.00"}%`;
      },
    },
    {
      key: "taxa_conversao_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-12" />;
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-16" />;
        return formatNumber(value);
      },
    },
    {
      key: "pedidos_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-12" />;
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-20" />;
        return formatCurrency(value);
      },
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-12" />;
        return <TrendIndicator value={value} />;
      },
    },
    {
      key: "receita_total",
      header: "Receita total",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-20" />;
        return formatCurrency(value);
      },
    },
    {
      key: "receita_total_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) => {
        if (origemMidiaLoading) return <Skeleton className="h-4 w-12" />;
        return <TrendIndicator value={value} />;
      },
    },
  ];

  if (origemMidiaError) {
    return (
      <div className="p-4 border rounded-lg bg-red-50">
        <p className="text-red-600">
          Erro ao carregar dados de Origem/Mídia: {origemMidiaError}
        </p>
      </div>
    );
  }

  if (!customerUuid) {
    return (
      <div className="bg-yellow-50 text-yellow-800 p-6 rounded-lg border border-yellow-200">
        <h3 className="font-semibold mb-2">Atenção</h3>
        <p className="text-sm">
          Nenhuma conta selecionada. Por favor, selecione uma conta para
          visualizar os dados.
        </p>
      </div>
    );
  }

  return (
    <StandardizedTable
      title="Origem / Mídia"
      description="Origem / mídia da sessão"
      columns={columns}
      data={origemMidiaData}
      total={origemMidiaTotal || undefined}
      loading={origemMidiaLoading}
      pagination={{
        current: origemMidiaPagination.current_page,
        pageSize,
        total: origemMidiaPagination.total_items,
        onPageChange: (page) => setCurrentPage(page),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

// Componente para a tabela "Campanhas" - COM DADOS REAIS
const CampanhasTable = ({
  searchTitle,
  searchTitleOp,
  searchPath,
  searchPathOp,
  campaignFiltersNonce,
}: PageSearchProps) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const mergedQuery: TableQueryDTO = React.useMemo(() => {
    const filters: TableQueryDTO["filters"] = [...(tableQuery.filters || [])];
    if (searchTitle.trim())
      filters.push({
        columnKey: "pageTitle",
        operator: searchTitleOp,
        value: searchTitle.trim(),
      });
    if (searchPath.trim())
      filters.push({
        columnKey: "pagePath",
        operator: searchPathOp,
        value: searchPath.trim(),
      });
    return { ...tableQuery, filters: filters.length ? filters : undefined };
  }, [tableQuery, searchTitle, searchTitleOp, searchPath, searchPathOp]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchTitle, searchPath]);

  const startDate = formatDateToISO(filterState.dateRange.startDate);
  const endDate = formatDateToISO(filterState.dateRange.endDate);
  const comparison =
    filterState.comparison?.type === "previousYear"
      ? "previous_year"
      : "previous_period";
  const customerUuid =
    localStorage.getItem("active_customer_uuid") ||
    selectedAccount?.customer_uuid ||
    selectedAccount?.id;
  const accountId = selectedAccount?.account_id ?? undefined;

  const {
    data,
    total,
    pagination: campaignsPagination,
    loading,
    error,
  } = usePagesCampaignsData(
    startDate,
    endDate,
    comparison,
    customerUuid || undefined,
    accountId || undefined,
    currentPage,
    10,
    mergedQuery,
    campaignFiltersNonce,
  );

  const renderDelta = (value: unknown): React.ReactNode => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num))
      return <span className="text-muted-foreground">—</span>;
    return <TrendIndicator value={num} />;
  };

  const columns: TableColumn[] = [
    {
      key: "campanha_sessao",
      header: "Campanha da sessão",
      align: "left",
      alwaysVisible: true,
      render: (value, item, index, isTotal) => {
        if (isTotal) return value;
        const displayValue = value || "N/D";
        return `${(currentPage - 1) * 10 + index + 1}. ${displayValue}`;
      },
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      key: "sessoes_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDelta,
    },
    {
      key: "usuarios",
      header: "Usuários",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      key: "usuarios_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDelta,
    },
    {
      key: "taxa_conversao",
      header: "TX Conversão",
      align: "right",
      render: (value) =>
        value !== null && value !== undefined
          ? `${Number(value).toFixed(2)}%`
          : "0.00%",
    },
    {
      key: "taxa_conversao_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDelta,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      key: "pedidos_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDelta,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "ticket_medio_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDelta,
    },
    {
      key: "receita_total",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      key: "receita_total_percent_change",
      header: "% Δ",
      align: "right" as const,
      render: renderDelta,
    },
  ];

  if (loading && data.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
        <h3 className="font-semibold mb-2">Erro ao carregar dados</h3>
        <p className="text-sm">{error}</p>
        <p className="text-sm mt-2">
          Não conseguimos processar esta informação. Verifique a conexão de seus
          dados na tela Integrações ou a configuração de suas fontes de dados
          nos devidos portais (Google, Meta, etc).
        </p>
      </div>
    );
  }

  if (!customerUuid) {
    return (
      <div className="bg-yellow-50 text-yellow-800 p-6 rounded-lg border border-yellow-200">
        <h3 className="font-semibold mb-2">Atenção</h3>
        <p className="text-sm">
          Nenhuma conta selecionada. Por favor, selecione uma conta para
          visualizar os dados.
        </p>
      </div>
    );
  }

  return (
    <StandardizedTable
      title="Campanhas"
      description="Campanhas da sessão"
      columns={columns}
      data={data}
      total={total || undefined}
      loading={loading}
      pagination={{
        current: currentPage,
        pageSize: campaignsPagination.pageSize,
        total: campaignsPagination.total,
        onPageChange: (pageNumber) => setCurrentPage(pageNumber),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

const PagesPage: React.FC = () => {
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const { selectedAccount } = useAccount();
  const { filter: filterState } = useGlobalFilter();
  const customerUuid =
    localStorage.getItem("active_customer_uuid") ||
    selectedAccount?.customer_uuid ||
    selectedAccount?.id ||
    null;
  const accountId = selectedAccount?.account_id ?? undefined;
  const [searchTitle, setSearchTitle] = useState("");
  const [searchTitleOp, setSearchTitleOp] =
    useState<TableFilterOperator>("contains");
  const [searchPath, setSearchPath] = useState("");
  const [searchPathOp, setSearchPathOp] =
    useState<TableFilterOperator>("contains");

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Páginas</h1>
        <p className="text-muted-foreground">
          Análise detalhada do desempenho das páginas do seu e-commerce
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={accountId}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Campos de busca globais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SearchField
          label="Título da página"
          value={searchTitle}
          operator={searchTitleOp}
          onValueChange={setSearchTitle}
          onOperatorChange={setSearchTitleOp}
        />
        <SearchField
          label="Caminho da página"
          value={searchPath}
          operator={searchPathOp}
          onValueChange={setSearchPath}
          onOperatorChange={setSearchPathOp}
        />
      </div>

      {/* Primeira seção: TODAS AS PÁGINAS */}
      <TodasAsPaginasTable
        searchTitle={searchTitle}
        searchTitleOp={searchTitleOp}
        searchPath={searchPath}
        searchPathOp={searchPathOp}
        campaignFiltersNonce={campaignFiltersNonce}
      />

      {/* Segunda seção: PÁGINA DE ENTRADA */}
      <PaginaEntradaTable
        searchTitle={searchTitle}
        searchTitleOp={searchTitleOp}
        searchPath={searchPath}
        searchPathOp={searchPathOp}
        campaignFiltersNonce={campaignFiltersNonce}
      />

      {/* Terceira seção: Agrupamento de canal padrão */}
      <AgrupamentoCanalTable campaignFiltersNonce={campaignFiltersNonce} />

      {/* Quarta seção: Origem / Mídia */}
      <OrigemMidiaTable
        searchTitle={searchTitle}
        searchTitleOp={searchTitleOp}
        searchPath={searchPath}
        searchPathOp={searchPathOp}
        campaignFiltersNonce={campaignFiltersNonce}
      />

      {/* Quinta seção: Campanhas */}
      <CampanhasTable
        searchTitle={searchTitle}
        searchTitleOp={searchTitleOp}
        searchPath={searchPath}
        searchPathOp={searchPathOp}
        campaignFiltersNonce={campaignFiltersNonce}
      />
    </div>
  );
};

export default PagesPage;
